import React, { useState, useEffect, useRef, useCallback } from 'react';
import { LIMITES } from '../limites';
import { Loader, CheckCircle, XCircle, Volume2 } from 'lucide-react';
import './TraduceOracion.css'; 

// --- COMPONENTE SVG PARA EL ICONO DE LA BOMBILLA ---
const BulbIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M15 14c.2-1 .7-1.7 1.5-2.5 1-.9 1.5-2.2 1.5-3.5A6 6 0 0 0 6 8c0 1 .2 2.2 1.5 3.5.7.8 1.3 1.5 1.5 2.5"/>
        <path d="M9 18h6"/>
        <path d="M10 22h4"/>
    </svg>
);

const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
const recognition = SpeechRecognition ? new SpeechRecognition() : null;

function TraduceOracion() {
    const [gameState, setGameState] = useState('setup');
    const [sentences, setSentences] = useState([]);
    const [currentSentence, setCurrentSentence] = useState(null);
    const [userAnswer, setUserAnswer] = useState('');
    const [score, setScore] = useState(0);
    const [sentenceNumber, setSentenceNumber] = useState(0);
    const [timeLeft, setTimeLeft] = useState(30);
    const [streak, setStreak] = useState(0);
    const [lives, setLives] = useState(5);
    const [feedback, setFeedback] = useState(null);
    const [correctAnswers, setCorrectAnswers] = useState(0);
    const [isLoading, setIsLoading] = useState(false);
    const [showHint, setShowHint] = useState(false);
    const [showCorrectionCard, setShowCorrectionCard] = useState(false);
    const [toastMessage, setToastMessage] = useState('');

    const [selectedLevel, setSelectedLevel] = useState('A1');
    const [selectedTopics, setSelectedTopics] = useState(['present_simple']);

    const inputRef = useRef(null);
    const timerRef = useRef(null);
    const hintTimerRef = useRef(null);
    const totalSentences = sentences.length > 0 ? sentences.length : 8;

    const levels = {
        'A1': { name: 'Principante', time: 30, multiplier: 1 },
        'A2': { name: 'Elemental', time: 60, multiplier: 1.2 },
        'B1': { name: 'Intermedio', time: 60, multiplier: 1.4 },
        'B2': { name: 'Avanzado', time: 90, multiplier: 1.6 },
        'C1': { name: 'Experto', time: 100, multiplier: 1.8 },
        'C2': { name: 'Maestría', time: 120, multiplier: 2.0 }
    };

    const topicOptions = [
        { id: 'present_simple', name: 'Presente Simple' }, { id: 'past_simple', name: 'Pasado Simple' },
        { id: 'future_simple', name: 'Futuro Simple (will)' }, { id: 'present_continuous', name: 'Presente Continuo' },
        { id: 'past_continuous', name: 'Pasado Continuo' }, { id: 'future_continuous', name: 'Futuro Continuo' },
        { id: 'present_perfect', name: 'Presente Perfecto' }, { id: 'past_perfect', name: 'Pasado Perfecto' },
        { id: 'future_perfect', name: 'Futuro Perfecto' }, { id: 'present_perfect_continuous', name: 'Presente Perfecto Continuo' },
        { id: 'past_perfect_continuous', name: 'Pasado Perfecto Continuo' }, { id: 'future_perfect_continuous', name: 'Futuro Perfecto Continuo' },
        { id: 'future_going_to', name: 'Futuro (going to)' }, { id: 'conditional_zero', name: 'Condicional Zero' },
        { id: 'conditional_first', name: 'Primer Condicional' }, { id: 'conditional_second', name: 'Segundo Condicional' },
        { id: 'conditional_third', name: 'Tercer Condicional' }, { id: 'wh_questions', name: 'WH Questions' },
        { id: 'connectors', name: 'Conectores' }, { id: 'phrasal_verbs', name: 'Phrasal Verbs' },
        { id: 'subjunctive', name: 'Subjuntivo' }
    ];

    const toggleTopic = (topicId) => {
        if (toastMessage) return;

        setSelectedTopics(prev => {
            const isSelected = prev.includes(topicId);
            if (isSelected) {
                return prev.filter(t => t !== topicId);
            } else {
                if (prev.length < 3) {
                    return [...prev, topicId];
                } else {
                    setToastMessage('Puedes seleccionar un máximo de 3 temas.');
                    setTimeout(() => setToastMessage(''), 3000);
                    return prev;
                }
            }
        });
    };

    // Detecta si la página se recargó o el usuario se deslogueó
    const isNewSession = !localStorage.getItem('lastSessionTime') || 
                        !localStorage.getItem('token') || 
                        new Date().getTime() - localStorage.getItem('lastSessionTime') > 24 * 60 * 60 * 1000; // 24 horas de tolerancia

    const fetchSentences = async () => {
        if (selectedTopics.length === 0) return;
        setIsLoading(true);
        setGameState('loading');

        let seenSentences = [];
        if (isNewSession) {
            // Nueva sesión: reinicia las oraciones vistas
            localStorage.removeItem('traduceOracionSeen');
        } else {
            // Sesión continua: usa las oraciones vistas previas
            const seenSentencesByConfig = JSON.parse(localStorage.getItem('traduceOracionSeen')) || {};
            const configKey = `${selectedLevel}-${selectedTopics.sort().join('-')}`;
            seenSentences = seenSentencesByConfig[configKey] || [];
        }

        try {
            const token = localStorage.getItem('token');
            // 🔥 CAMBIO CRÍTICO: URL RELATIVA en lugar de absoluta
            const response = await fetch('/api/minigames/get-traduce-oracion', {
                method: 'POST',
                headers: { 
                    'Content-Type': 'application/json', 
                    'Authorization': `Bearer ${token}` 
                },
                body: JSON.stringify({ 
                    level: selectedLevel, 
                    tenses: selectedTopics, 
                    seen_sentences: seenSentences,
                    request_new: !isNewSession && sentences.length === 0 // Solo pide nuevas si se agotaron y es una sesión continua
                }),
            });
            if (!response.ok) throw new Error('No se pudieron cargar las oraciones.');
            const data = await response.json();
            setSentences(prevSentences => [...prevSentences, ...data]); // Añade nuevas oraciones a las existentes
            startGame([...sentences, ...data]);
        } catch (error) {
            console.error(error);
            setGameState('setup');
        } finally {
            setIsLoading(false);
            // Actualiza el tiempo de la última sesión
            localStorage.setItem('lastSessionTime', new Date().getTime());
        }
    };
    
    const saveSeenSentences = useCallback(() => {
        if (isNewSession) return; // No guarda si es una nueva sesión
        const seenSentencesByConfig = JSON.parse(localStorage.getItem('traduceOracionSeen')) || {};
        const configKey = `${selectedLevel}-${selectedTopics.sort().join('-')}`;
        const currentSeen = seenSentencesByConfig[configKey] || [];
        const newSentences = sentences.map(s => s.spanish);
        seenSentencesByConfig[configKey] = [...new Set([...currentSeen, ...newSentences])];
        localStorage.setItem('traduceOracionSeen', JSON.stringify(seenSentencesByConfig));
    }, [sentences, selectedLevel, selectedTopics, isNewSession]);

    const handleEndGame = useCallback(() => {
        saveSeenSentences();
        setGameState('finished');
        
        // Guardar XP en el backend
        const saveScore = async () => {
            try {
                const token = localStorage.getItem('token');
                if (!token) return;
                await fetch('/api/minigames/save-score', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`
                    },
                    body: JSON.stringify({
                        game_name: 'traduce_oracion',
                        score: score,
                        correct_answers: correctAnswers,
                        total_questions: totalSentences
                    })
                });
            } catch (error) {
                console.error("Error guardando XP:", error);
            }
        };
        saveScore();
    }, [saveSeenSentences, score, correctAnswers, totalSentences]);
    
    const loadNextSentence = useCallback((sNumber, sList) => {
        if (sNumber < sList.length) {
            setCurrentSentence(sList[sNumber]);
            setUserAnswer('');
            setTimeLeft(levels[selectedLevel].time);
            setFeedback(null);
            setShowHint(false);
            setShowCorrectionCard(false);
            clearTimeout(hintTimerRef.current);
            setTimeout(() => inputRef.current?.focus(), 100);
        } else if (!isNewSession) {
            // Si se agotan y es sesión continua, solicita más
            fetchSentences();
        } else {
            handleEndGame();
        }
    }, [handleEndGame, selectedLevel, isNewSession]);

    const moveToNextSentence = () => {
        if (lives <= 0) {
            handleEndGame();
            return;
        }
        const nextSentenceNum = sentenceNumber + 1;
        setSentenceNumber(nextSentenceNum);
        loadNextSentence(nextSentenceNum, sentences);
    };

    const startGame = useCallback((gameSentences) => {
        setScore(0);
        setSentenceNumber(0);
        setStreak(0);
        setLives(5);
        setCorrectAnswers(0);
        setFeedback(null);
        setGameState('playing');
        loadNextSentence(0, gameSentences);
    }, [loadNextSentence]);

    useEffect(() => {
        if (gameState === 'playing' && !feedback && !showCorrectionCard) {
            if (timeLeft > 0) {
                timerRef.current = setTimeout(() => setTimeLeft(timeLeft - 1), 1000);
            } else {
                handleAnswer(true);
            }
        }
        return () => clearTimeout(timerRef.current);
    }, [timeLeft, gameState, feedback, showCorrectionCard]);

    const handleAnswer = useCallback((isTimeOut = false) => {
        if (feedback || showCorrectionCard) return;

        let isCorrect = false;
        if (!isTimeOut) {
            const normalize = (str) => str
                .toLowerCase() // Asegura que todo sea minúscula
                .trim()
                .replace(/[.,!?;]/g, ''); // Solo elimina puntuación, preserva contracciones
            const userNorm = normalize(userAnswer);
            const correctNorm = normalize(currentSentence.correct);
            const alternativesNorm = currentSentence.alternatives.map(normalize);
            isCorrect = userNorm === correctNorm || alternativesNorm.includes(userNorm);
            console.log('User:', userNorm, 'Correct:', correctNorm, 'Alternatives:', alternativesNorm); // Depuración
        }

        clearTimeout(hintTimerRef.current);
        setShowHint(false);

        if (isCorrect) {
            const points = Math.round(150 * levels[selectedLevel].multiplier);
            setScore(s => s + points);
            setStreak(s => s + 1);
            setCorrectAnswers(c => c + 1);
            setFeedback({ type: 'success', message: `¡Perfecto! +${points}` });
            setTimeout(moveToNextSentence, 1500);
        } else {
            setStreak(0);
            setLives(l => l - 1);
            setShowCorrectionCard(true);
        }
    }, [userAnswer, currentSentence, feedback, showCorrectionCard, sentenceNumber, sentences, loadNextSentence, selectedLevel]);
    
    const handleKeyPress = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            if (showCorrectionCard) {
                moveToNextSentence();
            } else if (userAnswer.trim() && !feedback) {
                handleAnswer();
            }
        }
    };

    const handleShowHint = () => {
        if (showHint) return;
        setShowHint(true);
        hintTimerRef.current = setTimeout(() => setShowHint(false), 5000);
    };

    // --- Funcionalidad de Hablar (Speech-to-Text) ---
    const [isListening, setIsListening] = useState(false);
    const [transcript, setTranscript] = useState('');

    const handleToggleListening = () => {
        if (!recognition) {
            alert("Lo siento, tu navegador no soporta el reconocimiento de voz. Usa Chrome o Edge.");
            return;
        }
        if (isListening) {
            recognition.stop();
        } else {
            setTranscript('');
            recognition.lang = 'en-US'; // Ajusta a 'es-ES' si prefieres español
            recognition.start();
        }
    };

    useEffect(() => {
        if (!recognition || !currentSentence) return;

        recognition.onstart = () => setIsListening(true);
        recognition.onend = () => setIsListening(false);
        recognition.onresult = (event) => {
            const currentTranscript = event.results[0][0].transcript;
            setTranscript(currentTranscript);
            setUserAnswer(currentTranscript); // Actualiza el textarea con la transcripción
        };
        recognition.onerror = (event) => {
            console.error("Speech recognition error:", event.error);
            setIsListening(false);
        };

        return () => {
            recognition.onstart = null;
            recognition.onend = null;
            recognition.onresult = null;
            recognition.onerror = null;
        };
    }, [currentSentence]);

    // --- Funcionalidad de Escuchar (Text-to-Speech) ---
    const speak = (text) => {
        if ('speechSynthesis' in window) {
            const utterance = new SpeechSynthesisUtterance(text);
            utterance.lang = 'es-ES'; // Pronuncia la oración en español
            window.speechSynthesis.speak(utterance);
        } else {
            alert("Lo siento, tu navegador no soporta la síntesis de voz.");
        }
    };

    const renderSetup = () => (
        <div className="to-setup">
            <h2>Configurar Juego</h2>
            <div className="to-options">
                <div className="to-option-group">
                    <h3>Nivel de Dificultad</h3>
                    <div className="to-level-buttons">
                        {Object.entries(levels).map(([level, info]) => (
                            <button key={level} onClick={() => setSelectedLevel(level)} className={`to-btn ${selectedLevel === level ? 'active' : ''}`}>
                                {level}
                            </button>
                        ))}
                    </div>
                </div>
                <div className="to-option-group">
                    <div className="to-topic-header">
                        <h3>Temas a Practicar</h3>
                        <small className="to-helper-text">Elige 1 - 3 temas max.</small>
                    </div>
                    <div className="to-topic-grid">
                        {topicOptions.map((topic) => (
                            <button key={topic.id} onClick={() => toggleTopic(topic.id)} className={`to-btn to-topic-btn ${selectedTopics.includes(topic.id) ? 'active' : ''}`}>
                                {topic.name}
                            </button>
                        ))}
                    </div>
                </div>
            </div>
            <div className="to-summary-box">
                <div><span>{totalSentences}</span> Oraciones</div>
                <div><span>{levels[selectedLevel].time}s</span> Por Oración</div>
                <div><span>IA</span> Análisis</div>
                <div><span>x{levels[selectedLevel].multiplier}</span> Multiplicador</div>
            </div>
            <button onClick={fetchSentences} disabled={isLoading || selectedTopics.length === 0} className="to-start-btn">
                {isLoading ? <><Loader className="animate-spin"/> Cargando...</> : 'Comenzar'}
            </button>
        </div>
    );
    
    const renderCorrectionCard = () => {
        const isLongAnswer = currentSentence.correct.length > 25;
        return (
            <div className="to-feedback-overlay">
                <div className={`to-feedback-card ${isLongAnswer ? 'long-answer' : ''}`}>
                    <XCircle size={48} className="error-icon" />
                    <h2>¡Respuesta Incorrecta!</h2>
                    <p>La respuesta correcta es:</p>
                    <strong>"{currentSentence.correct}"</strong>
                    <button className="to-start-btn" onClick={moveToNextSentence}>Continuar</button>
                </div>
            </div>
        );
    };

    const renderGame = () => (
        <div className="to-game-container">
            {showCorrectionCard && renderCorrectionCard()}
            <div className="to-game-header">
                <div>Oración {sentenceNumber + 1} de {totalSentences}</div>
                <div className="to-stats">
                    <span>❤️ {lives}</span>
                    <span>Racha: {streak}</span>
                    <span>⭐ {score}</span>
                </div>
                <div className={`to-timer ${timeLeft <= 10 ? 'warning' : ''}`}>{timeLeft}s</div>
            </div>
            <div className="to-progress-bar-container"><div className="to-progress-bar" style={{width: `${(timeLeft / levels[selectedLevel].time) * 100}%`}}></div></div>
            
            <div className="to-sentence-card">
                <p>Traduce al inglés:</p>
                <h3>"{currentSentence.spanish}"</h3>
            </div>
            
            {showHint && <div className="to-hint-box">💡 {currentSentence.hint}</div>}

            <div className="to-input-area">
                <textarea
                    maxLength={LIMITES.respuestaEjercicio}
                    ref={inputRef}
                    value={userAnswer}
                    onChange={(e) => setUserAnswer(e.target.value)}
                    onKeyDown={handleKeyPress}
                    placeholder="Escribe o di tu traducción aquí..."
                    disabled={!!feedback || showCorrectionCard}
                    rows="3"
                    className="to-textarea"
                />
                <div className="to-action-buttons">
                    <button onClick={() => speak(currentSentence.spanish)} className="to-speak-btn" disabled={!!feedback || showCorrectionCard}>
                        <Volume2 size={24} />
                    </button>
                    <button onClick={handleToggleListening} disabled={!recognition || !!feedback || showCorrectionCard || isListening} className={`to-mic-btn ${isListening ? 'listening' : ''} ${!recognition ? 'disabled' : ''}`}>
                        🎤
                    </button>
                    <button onClick={() => handleAnswer()} disabled={!userAnswer.trim() || !!feedback || showCorrectionCard} className="to-confirm-btn">
                        Confirmar
                    </button>
                    <button onClick={handleShowHint} disabled={!!feedback || showHint || showCorrectionCard} className="to-hint-btn">
                        <BulbIcon />
                    </button>
                </div>
            </div>

            {feedback && feedback.type === 'success' && (
                <div className={`to-feedback ${feedback.type}`}>
                    <p>{feedback.message}</p>
                </div>
            )}
        </div>
    );

    const renderFinished = () => (
        <div className="to-setup">
            <h2>{lives <= 0 ? '¡Juego Terminado! (Sin vidas)' : '¡Juego Terminado!'}</h2>
            <div className="to-final-stats">
                <div><span>{score}</span> Puntos</div>
                <div><span>{correctAnswers}/{totalSentences}</span> Correctas</div>
            </div>
            <button onClick={() => setGameState('setup')} className="to-start-btn">Jugar de Nuevo</button>
        </div>
    );

    return (
        <div className="traduce-oracion-container">
            {toastMessage && <div className="to-toast-notification">{toastMessage}</div>}
            <div className={`traduce-oracion-inner-container ${gameState}`}>
                {gameState === 'setup' && renderSetup()}
                {gameState === 'loading' && <div className="to-loading-view"><Loader className="animate-spin" size={48} /><p>Cargando...</p></div>}
                {gameState === 'playing' && currentSentence && renderGame()}
                {gameState === 'finished' && renderFinished()}
            </div>
        </div>
    );
}

export default TraduceOracion;